import text from './text.json'
import data from './data.json'
import {
    CopyIcon,
    EnableWebhookIcon,
    ExpandDownIcon,
    HelpIcon,
    InstructionIcon,
    LoadMoreIcon,
    NoEventsIcon,
    PlusIcon
} from '../../icons'
import {ImgButton, SimpleBlueButton} from '../../components/core/button'
import Tooltip from '../../components/core/tooltip'
import {colors} from '../../utils/colors'
import {useEffect, useRef, useState} from 'react'
import renderSkeletonLoaders from '../../utils/loader'
import {useDispatch, useSelector} from 'react-redux'
import {getWebhook, getWebhookEvents, setIsLoadingEvents} from '../../stores/incident-management'
import ButtonLoader from '../../components/core/button/ButtonLoader'
import Modal from '../../components/core/modal'
import DateHelper from '../../utils/date'
import TextHelper from '../../utils/text'
import InstructionModal from '../../components/pages/job/Instructions'
import bash from './data/bash.json'
import python from './data/python.json'
import java from './data/java.json'
import node from './data/node.json'
import php from './data/php.json'
import ruby from './data/ruby.json'
import go from './data/go.json'
import csharp from './data/csharp.json'
import kotlin from './data/kotlin.json'
import scala from './data/scala.json'
import JsonView from '@uiw/react-json-view'
import {monokaiTheme} from '@uiw/react-json-view/monokai'
import {isValidJson} from '../../utils/object'
import Badge from '../../components/core/badge'
import LimitManager from '../../utils/limitManager'
import ForPaid from '../../components/core/forPaid'
import classNames from 'classnames'

const URL_TYPE = ':webhook_url'
const RETRY_DELAY = 5000
const INACTIVE_DELAY = 60000
const RETRY_LIMIT = 100
const CLICKED = true
const json = {
    value: 'application/json',
    type: 'json-check'
}

const EnableWebhook = ({setHoveredKeyValue, formikRef, rulesetsFormRef, isDarkMode, noPlan}) => {
    const languagesData = {bash, python, java, node, php, ruby, go, csharp, kotlin, scala}
    const dispatch = useDispatch()
    const jsonViewRef = useRef(null)
    const webhook = useSelector(state => state.incident_management.webhook)
    const webhookEvents = useSelector(state => state.incident_management.webhookEvents)
    const isButtonLoaderActive = useSelector(state => state.incident_management.buttonLoaderIsActive)
    const isLoadingEvents = useSelector(state => state.incident_management.isLoadingEvents)
    const isLoadMoreEnabled = useSelector(state => state.incident_management.isLoadMoreEnabled)
    const [isFirstLoading, setIsFirstLoading] = useState(true)
    const [isWebhookLoading, setIsWebhookLoading] = useState(false)
    const [instructionModal, setInstructionModal] = useState(false)
    const [isListening, setIsListening] = useState(false)
    const isWebhookEnabled = LimitManager.getWebhookIsEnabled()
    const [isEnabled, setIsEnabled] = useState(false)
    const [activeJsonIndex, setActiveJsonIndex] = useState(-1)
    const currentDate = DateHelper.convertToTimeZone(new Date())
    const [webhookEventsHeight, setWebhookEventsHeight] = useState(null)
    const lastIndexLimit = webhookEvents?.length - 3

    const toggleInstructionModal = () => {
        setInstructionModal(!instructionModal)
    }

    const toggleJsonData = (index) => {
        setActiveJsonIndex(index === activeJsonIndex ? -1 : index)
    }

    const tableHeader = [
        `<span>${text.ip}</span>`,
        `<span>${text.content_type_text}</span>`,
        `<span>${text.event_time}</span>`,
        '<span/>'
    ]

    const formatKeyName = (keys) => {
        return keys.map((key, index) => typeof key === 'number'
            ? `[${key}]`
            : (index === 0 ? `${key}` : `.${key}`)
        ).join('')
    }

    const handleLineHover = (value, keys, clicked = false) => {
        const keyName = formatKeyName(keys)
        if (formikRef?.current?.values?.content_type?.value === json.type) {
            setHoveredKeyValue({key: keyName, value: value, clicked})
        }
    }

    const tbodyData = webhookEvents?.map((event, index) => {
        const isJsonDataActive = index === activeJsonIndex
        const contentType = data.content_types.find(item => item.value === event?.content_type)

        return <tr key={index} ref={jsonViewRef}>
            <div className="table-data" onClick={() => toggleJsonData(index)}>
                <td><span className="min-width-150">{event?.ip}</span></td>
                <td><span className="min-width-150"><Badge color={contentType?.color} className={'lowercase'}>{contentType?.label}</Badge></span></td>
                <td><span className="min-width-250">{DateHelper.convertDateToUTC(event?.received_at)}</span></td>
                <td><span className={`${isJsonDataActive ? 'active' : ''}`}><ExpandDownIcon/></span></td>
            </div>
            {isJsonDataActive && (
                <span className="json-object" >
                    {event?.content_type === json.value ? <JsonView
                        value={isValidJson(event?.body) ? JSON.parse(event?.body) : {}}
                        style={monokaiTheme}
                        displayObjectSize={false}
                        enableClipboard
                        displayDataTypes={false}
                    >
                        <JsonView.Row
                            as="div"
                            render={(props, {keyName, value, keys}) => {
                                return (
                                    <div
                                        {...props}
                                        onClick={() => handleLineHover(value, keys, CLICKED)}
                                        onMouseEnter={() => handleLineHover(value, keys)}
                                        onMouseLeave={() => setHoveredKeyValue(null)}
                                    />
                                )
                            }}
                        />
                    </JsonView>
                        :
                        event?.body}
                </span>
            )}
        </tr>
    })

    const copyToClipboard = () => {
        TextHelper.copyText(webhook?.url)
    }

    const loadMoreWebhookEvents = (isLoadMore = true) => {
        const lastReceivedAt = webhookEvents?.reduce((prev, current) => {
            const prevDate = new Date(prev?.received_at)
            const currentDate = new Date(current?.received_at)
            return prevDate < currentDate ? prev : current
        })?.received_at

        if (!lastReceivedAt) {
            return
        }

        dispatch(getWebhookEvents(lastReceivedAt, isLoadMore, false))
    }

    const handleListening = (bool) => {
        setIsListening(bool)
    }

    const handleEnableWebhook = async() => {
        setIsEnabled(true)
        await dispatch(getWebhook(false))
    }

    useEffect(() => {
        if (!isWebhookEnabled) {
            dispatch(setIsLoadingEvents(false))
        }
        setIsWebhookLoading(true)
        dispatch(getWebhook())
        setIsFirstLoading(false)
    }, [])

    useEffect(() => {
        if (webhook) {
            dispatch(getWebhookEvents(currentDate, false, true))
            setIsEnabled(true)
        }
        setIsWebhookLoading(false)
    }, [webhook?.url])

    useEffect(() => {
        let interval
        let count = 0

        const retrieveEvents = async() => {
            count++
            if (count > RETRY_LIMIT) {
                setIsListening(false)
                clearInterval(interval)
                return
            }

            dispatch(getWebhookEvents(currentDate, false, false))
        }

        interval = setInterval(retrieveEvents, isListening ? RETRY_DELAY : INACTIVE_DELAY)

        return () => clearInterval(interval)
    }, [isListening])

    useEffect(() => {
        const setInitialScroll = () => {
            if (rulesetsFormRef?.current) {
                setWebhookEventsHeight(rulesetsFormRef?.current?.offsetHeight)
            }
        }
        setInitialScroll()
        window.addEventListener('resize', setInitialScroll)

        return () => {
            window.removeEventListener('resize', setInitialScroll)
        }
    }, [rulesetsFormRef?.current])

    useEffect(() => {
        if (jsonViewRef.current && activeJsonIndex >= lastIndexLimit && lastIndexLimit > 0) {
            jsonViewRef.current.scrollIntoView({block: 'end'})
        }
    }, [activeJsonIndex])

    return (
        <div className={`webhook-events rulesets-form ${!isEnabled && isWebhookEnabled ? 'justify-content-center' : ''}`} style={{minHeight: webhookEventsHeight}}>
            {!isWebhookEnabled && !noPlan && <span className="paid-container"><ForPaid forPaidText={text.for_paid} isExceeded={false}/></span> }
            <Modal isOpen={instructionModal}
                contentClassName="instruction-modal overflow-auto"
                title={text.instructions}
                closeModal={toggleInstructionModal}>
                <InstructionModal url={webhook?.url} data={languagesData} urlType={URL_TYPE}/>
            </Modal>
            {isFirstLoading || isLoadingEvents || (isEnabled && !webhookEvents && !webhookEvents?.length) || isWebhookLoading ? <span className="events-loader">{renderSkeletonLoaders(16)}</span> :
                isEnabled && isWebhookEnabled ? (
                    <div className={classNames('webhooks', {
                        dark: isDarkMode
                    })}>
                        <span className="webhook-header">{text.webhook_request_text}</span>
                        <div className={classNames('enabled-webhooks', {
                            dark: isDarkMode
                        })}>
                            <div className={classNames('webhook-url-actions', {
                                dark: isDarkMode
                            })}>
                                <div className="webhook-url">
                                    <input className="url-text" value={webhook?.url} readOnly/>
                                    <span className="url-copy" onClick={copyToClipboard}>
                                        <CopyIcon />
                                        <span className="copy">{text.copy}</span>
                                    </span>
                                </div>
                                <span className={classNames('open-book', {
                                    dark: isDarkMode
                                })} onClick={toggleInstructionModal}>
                                    <InstructionIcon />
                                </span>
                                <Tooltip
                                    placement="left"
                                    trigger="click"
                                    content={text.webhook_url_info}
                                >
                                    <HelpIcon stroke={colors.colorBgFilter} width="22" height="22" />
                                </Tooltip>
                            </div>
                            <div className="listening-buttons">
                                <SimpleBlueButton
                                    className={`webhook-button ${!isListening ? 'start-button-expanded' : 'start-button'}`}
                                    onClick={() => handleListening(true)}
                                    disabled={isListening}>
                                    {isListening && <ButtonLoader />}
                                    {isListening ? text.loading : text.start_to_listen}
                                </SimpleBlueButton>
                                {isListening &&
                <SimpleBlueButton
                    className={`webhook-button stop-button ${isListening ? 'stop-button-expanded' : ''}`}
                    onClick={() => handleListening(false)}>
                    {text.stop}
                </SimpleBlueButton>}
                            </div>
                            {webhookEvents?.length ?
                                <>
                                    <span className="conditions-header">{text.conditions_text}</span>
                                    <div className={classNames('table-container', {
                                        dark: isDarkMode
                                    })}>
                                        <table>
                                            <thead>
                                                <tr>
                                                    {tableHeader.map((header, index) => (
                                                        <th key={index} dangerouslySetInnerHTML={{__html: header}} />
                                                    ))}
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {tbodyData}
                                            </tbody>
                                        </table></div>

                                    {isLoadMoreEnabled &&
                            <ImgButton
                                onClick={() => loadMoreWebhookEvents()}
                                type="button"
                                className={classNames('load-button', {
                                    active: isButtonLoaderActive,
                                    dark: isDarkMode
                                })}
                            >
                                <span className="load-more-text">{text.load_more}</span>
                                {isLoadMoreEnabled && !isButtonLoaderActive ? <PlusIcon stroke={colors.colorMuted}/> : <LoadMoreIcon stroke={colors.colorMuted}/>}
                            </ImgButton>}
                                </> :
                                <div className={classNames('no-events', {
                                    dark: isDarkMode
                                })}>
                                    <NoEventsIcon className="events-icon"/>
                                    <span className="no-events-text">{text.no_events_text}</span>
                                </div>}
                        </div>
                    </div>
                ) : (
                    <>
                        <span className={classNames('mr-auto', {
                            dark: isDarkMode
                        })}>
                            <EnableWebhookIcon className="events-icon"/>
                        </span>
                        <span className={classNames('events-text mr-auto', {
                            dark: isDarkMode
                        })}>
                            {text.enable_webhook_text}
                        </span>
                        <SimpleBlueButton
                            className={'webhook-button mr-auto'}
                            onClick={handleEnableWebhook}
                            disabled={!isWebhookEnabled}
                        >
                            {text.enable}
                        </SimpleBlueButton>
                    </>
                )}
        </div>
    )
}

export default EnableWebhook
